import { useLayoutEffect, useRef, useState } from 'react'

import { truncateLabelToWidth } from '@/features/resource/utils/truncateLabelToWidth'

/**
 * Fits a string into a width inside an SVG, cutting it with an ellipsis when it does not.
 *
 * SVG text has no `text-overflow`, and only a rendered element can say how wide a string is, so the
 * fitting happens after layout against the element itself. jsdom has neither layout nor
 * `getComputedTextLength`, so there the text stays whole and the clip path behind this holds it.
 */
export default function useTruncatedSvgText(fullText: string, maxWidth: number) {
  const ref = useRef<SVGTextElement | null>(null)
  const [displayText, setDisplayText] = useState(fullText)

  useLayoutEffect(() => {
    let cancelled = false

    const fit = () => {
      const label = ref.current

      if (cancelled || label === null || typeof label.getComputedTextLength !== 'function') {
        return
      }

      const fitted = truncateLabelToWidth(fullText, maxWidth, (candidate) => {
        label.textContent = candidate

        return label.getComputedTextLength()
      })

      /*
       * Written back here rather than left to the re-render: measuring puts candidates into the
       * live element, and when new text fits the same way React sees no state change and does not
       * re-render, leaving whichever candidate was measured last on screen.
       */
      label.textContent = fitted
      setDisplayText(fitted)
    }

    fit()

    /*
     * A web font arriving after first paint changes every width, so the first measurement was taken
     * against the fallback face. `document.fonts` is absent in jsdom, hence the optional call. The
     * cancellation is about the closure: `fit` holds the text this run was set up for, so a promise
     * left over from earlier text would measure and write a string that is no longer current.
     */
    document.fonts?.ready.then(fit).catch(() => undefined)

    return () => {
      cancelled = true
    }
  }, [fullText, maxWidth])

  return { ref, displayText, isTruncated: displayText !== fullText }
}
